/**
 * localStorage-backed stats. Schema-versioned so future migrations are clean.
 * No PII, no accounts.
 */

import type { LocalStats, PuzzleResult } from '@/types/stats';
import { STATS_HISTORY_CAP, STATS_STORAGE_KEY } from '@/types/stats';
import { localStatsSchema } from '@/schemas/stats.schema';
import { shiftYmd } from './melbourne';

export function defaultStats(): LocalStats {
  return {
    schemaVersion: 1,
    streak: 0,
    maxStreak: 0,
    totalPlayed: 0,
    totalWon: 0,
    lastPlayedDate: null,
    history: [],
  };
}

export function loadStats(): LocalStats {
  if (typeof localStorage === 'undefined') return defaultStats();
  const raw = localStorage.getItem(STATS_STORAGE_KEY);
  if (!raw) return defaultStats();
  try {
    const parsed = JSON.parse(raw);
    const result = localStatsSchema.safeParse(parsed);
    if (result.success) return result.data;
    return defaultStats();
  } catch {
    return defaultStats();
  }
}

export function saveStats(stats: LocalStats): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STATS_STORAGE_KEY, JSON.stringify(stats));
  } catch {
    // Quota exceeded or storage disabled — silently ignore.
  }
}

/**
 * Record a completed puzzle. Only winning plays mutate stats. Replaying the
 * same date is a no-op for streak math.
 */
export function recordResult(prev: LocalStats, result: PuzzleResult): LocalStats {
  // Replay protection: same date already recorded → no change.
  if (prev.lastPlayedDate === result.date) return prev;

  // Streak math:
  //  - First-ever play → streak = 1
  //  - lastPlayedDate is yesterday → streak += 1
  //  - lastPlayedDate is older than yesterday → streak resets to 1
  let newStreak: number;
  if (prev.lastPlayedDate === null) {
    newStreak = 1;
  } else {
    const yesterday = shiftYmd(result.date, -1);
    newStreak = prev.lastPlayedDate === yesterday ? prev.streak + 1 : 1;
  }

  const newHistory = [...prev.history, result].slice(-STATS_HISTORY_CAP);

  return {
    schemaVersion: 1,
    streak: newStreak,
    maxStreak: Math.max(prev.maxStreak, newStreak),
    totalPlayed: prev.totalPlayed + 1,
    totalWon: prev.totalWon + 1,
    lastPlayedDate: result.date,
    history: newHistory,
  };
}

/** Has the user already played today's puzzle? */
export function hasPlayedDate(stats: LocalStats, date: string): boolean {
  return stats.lastPlayedDate === date;
}

/**
 * Streak milestone events fire at 3, 7, 14, 30, 60, 100. Returns the milestone
 * just hit, or null.
 */
export function streakMilestone(prevStreak: number, newStreak: number): number | null {
  const milestones = [3, 7, 14, 30, 60, 100];
  for (const m of milestones) {
    if (newStreak >= m && prevStreak < m) return m;
  }
  return null;
}
